// Chamadas do Programa de Indicações (#6196). Tudo passa por funções do banco
// (fn_indicacao_*): elas aplicam quem vê o quê e a regra de duplicidade. A tela nunca
// grava direto nas tabelas.
import { supabase } from '../lib/supabase';
import type {
  IndicacaoAndamento,
  IndicacaoCanal,
  IndicacaoChecagem,
  IndicacaoDaNegociacao,
  IndicacaoFicha,
  IndicacaoItem,
  IndicacaoKpisMes,
  IndicacaoPessoaBusca,
  IndicacaoPessoaResumo,
  IndicacaoPrograma,
  IndicacaoRecompensa,
  IndicacaoResultadoAbordagem,
  IndicacaoResultadoRegistro,
  RecompensaStatus,
  RecompensaTipo,
} from '../../types';

/** Bucket PRIVADO (migration 20261020100000): download só por URL assinada. */
export const BUCKET_COMPROVANTES = 'indicacoes-comprovantes';

async function rpc<T>(nome: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(nome, args);
  if (error) throw new Error(error.message);
  return data as T;
}

export const kpisDoMes = () => rpc<IndicacaoKpisMes>('fn_indicacao_kpis_mes');

export type FiltroPessoa = 'todos' | 'nunca_abordados' | 'voltar_depois' | 'abordados_ha_mais_de' | 'clientes' | 'nao_clientes';

export interface FiltroPessoas {
  busca?: string;
  filtro?: FiltroPessoa;
  dias?: number;
  limite?: number;
  offset?: number;
}

export const listarPessoas = (f: FiltroPessoas) =>
  rpc<IndicacaoPessoaResumo[]>('fn_indicacao_listar_pessoas', {
    p_busca: f.busca || null,
    p_filtro: f.filtro ?? 'todos',
    p_dias: f.dias ?? 90,
    p_limite: f.limite ?? 50,
    p_offset: f.offset ?? 0,
  });

export const buscarPessoa = (termo: string) =>
  rpc<IndicacaoPessoaBusca[]>('fn_indicacao_buscar_pessoa', { p_termo: termo });

export const fichaDaPessoa = (alvo: { pessoaId: string | null; cpfCnpj: string | null }) =>
  rpc<IndicacaoFicha>('fn_indicacao_ficha', { p_pessoa_id: alvo.pessoaId, p_cpf_cnpj: alvo.cpfCnpj });

export const verificarIndicado = (a: {
  whatsapp: string;
  cpfCnpj?: string | null;
  indicadorWhatsapp?: string | null;
  indicadorCpfCnpj?: string | null;
}) =>
  rpc<IndicacaoChecagem>('fn_indicacao_verificar', {
    p_whatsapp: a.whatsapp,
    p_cpf_cnpj: a.cpfCnpj || null,
    p_indicador_whatsapp: a.indicadorWhatsapp || null,
    p_indicador_cpf_cnpj: a.indicadorCpfCnpj || null,
  });

/** Pessoa já cadastrada ({ id }) ou nova ({ nome, whatsapp, cpf_cnpj }). */
export interface PessoaParaRegistro {
  id?: string | null;
  nome?: string;
  cpf_cnpj?: string | null;
  whatsapp?: string | null;
}

export interface IndicadoParaRegistro {
  nome: string;
  whatsapp: string;
  cpf_cnpj?: string | null;
  cidade?: string | null;
  valor_conta?: number | null;
}

export const registrarAbordagem = (a: {
  pessoa: PessoaParaRegistro;
  canal: IndicacaoCanal;
  resultado: IndicacaoResultadoAbordagem;
  observacao?: string;
  indicados: IndicadoParaRegistro[];
}) =>
  rpc<IndicacaoResultadoRegistro>('fn_indicacao_registrar_abordagem', {
    p_pessoa: a.pessoa,
    p_canal: a.canal,
    p_resultado: a.resultado,
    p_observacao: a.observacao?.trim() || null,
    p_indicados: a.indicados,
  });

export const vincularEspontanea = (negociacaoId: number, pessoa: PessoaParaRegistro) =>
  rpc<{ indicacao_id: number; pessoa_id: string }>('fn_indicacao_vincular_espontanea', {
    p_negociacao_id: negociacaoId,
    p_pessoa: pessoa,
  });

export interface FiltroIndicacoes {
  busca?: string;
  andamento?: IndicacaoAndamento | '';
  limite?: number;
  offset?: number;
}

export const listarIndicacoes = (f: FiltroIndicacoes) =>
  rpc<IndicacaoItem[]>('fn_indicacao_listar', {
    p_busca: f.busca || null,
    p_andamento: f.andamento || null,
    p_limite: f.limite ?? 50,
    p_offset: f.offset ?? 0,
  });

export const indicacaoDaNegociacao = (negociacaoId: number) =>
  rpc<IndicacaoDaNegociacao | null>('fn_indicacao_da_negociacao', { p_negociacao_id: negociacaoId });

export async function programaAtivo(): Promise<IndicacaoPrograma | null> {
  const { data, error } = await supabase.from('indicacao_programas').select('*').eq('ativo', true).maybeSingle();
  if (error) throw new Error(error.message);
  return data as IndicacaoPrograma | null;
}

export interface ProgramaParaSalvar {
  recompensaClienteTipo: RecompensaTipo | null;
  valorCliente: number | null;
  recompensaNaoClienteTipo: 'dinheiro' | 'brinde';
  valorNaoCliente: number | null;
  descricaoBrinde: string | null;
}

export const salvarPrograma = (p: ProgramaParaSalvar) =>
  rpc<void>('fn_indicacao_salvar_programa', {
    p_recompensa_cliente_tipo: p.recompensaClienteTipo,
    p_valor_cliente: p.valorCliente,
    p_recompensa_nao_cliente_tipo: p.recompensaNaoClienteTipo,
    p_valor_nao_cliente: p.valorNaoCliente,
    p_descricao_brinde: p.descricaoBrinde,
  });

export const listarRecompensas = (status: RecompensaStatus | 'a_definir' | '') =>
  rpc<IndicacaoRecompensa[]>('fn_indicacao_listar_recompensas', { p_status: status || null });

export const ajustarRecompensa = (id: number, tipo: RecompensaTipo, valor: number, motivo: string) =>
  rpc<void>('fn_indicacao_ajustar_recompensa', { p_recompensa_id: id, p_tipo: tipo, p_valor: valor, p_motivo: motivo });

export const atualizarPagamento = (pessoaId: string, cpfCnpj: string | null, chavePix: string | null) =>
  rpc<void>('fn_indicacao_atualizar_pagamento', { p_pessoa_id: pessoaId, p_cpf_cnpj: cpfCnpj, p_chave_pix: chavePix });

export const pagarRecompensa = (id: number, comprovantePath: string | null, observacao: string | null) =>
  rpc<void>('fn_indicacao_pagar', { p_recompensa_id: id, p_comprovante_path: comprovantePath, p_observacao: observacao });

export const cancelarRecompensa = (id: number, motivo: string) =>
  rpc<void>('fn_indicacao_cancelar_recompensa', { p_recompensa_id: id, p_motivo: motivo });

/** Sobe o comprovante e devolve o CAMINHO (o banco confere o prefixo recompensas/<id>/). */
export async function enviarComprovante(recompensaId: number, arquivo: File): Promise<string> {
  const seguro = arquivo.name.replace(/[^\w.\-]+/g, '_');
  const caminho = `recompensas/${recompensaId}/${Date.now()}-${seguro}`;
  const { error } = await supabase.storage.from(BUCKET_COMPROVANTES).upload(caminho, arquivo, { upsert: false });
  if (error) throw new Error(`Não foi possível enviar o comprovante: ${error.message}`);
  return caminho;
}

/** URL assinada de 5 minutos. */
export async function urlDoComprovante(caminho: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_COMPROVANTES).createSignedUrl(caminho, 300);
  if (error || !data) throw new Error(`Não foi possível abrir o comprovante: ${error?.message ?? 'sem URL'}`);
  return data.signedUrl;
}
